import { useGlobalToasts } from '@src/hooks/Toast/useGlobalToasts';
import { useEventListener } from '@src/hooks/useEventListener';
import type { editor } from 'monaco-editor';
import { useCallback, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { defineMessages } from 'react-intl';
import { copyText } from '../../utils/copyText';
import { isApplePlatform } from '../../utils/isApplePlatform';
import { FormattedMessage } from '../Intl/FormattedMessage';
import { NotificationVariants } from '../Notification/types';
import { Tab, TabAppearance, TabList, Tabs, TabSize } from '../Tabs';
import { Text } from '../Text';
import { hasNonEmptyEditorSelection, JsonMonacoEditor } from './JsonMonacoEditor';
import {
  JsonModalContent,
  JsonModalCopyHint,
  JsonModalHeader,
  JsonModalSourcePre,
  JsonModalTabBar,
  JsonModalTabPanel,
} from './styles';
import type { JsonModalResolvedDocument } from './types';

const messages = defineMessages({
  jsonCopiedToClipboard: {
    defaultMessage: 'JSON copied to clipboard',
    id: 'JsonModal.jsonCopiedToClipboard',
  },
  copyShortcutHint: {
    defaultMessage: '{shortcut} to copy',
    id: 'JsonModal.copyShortcutHint',
  },
});

function copyDocumentShortcutLabel(): string {
  return isApplePlatform() ? '⌘C' : 'Ctrl+C';
}

function tabElementId(documentId: string): string {
  return `json-modal-tab-${documentId}`;
}

function nextDocumentIndex(selectedIndex: number, documentCount: number, key: string): number | undefined {
  switch (key) {
    case 'ArrowRight':
      return (selectedIndex + 1) % documentCount;
    case 'ArrowLeft':
      return (selectedIndex - 1 + documentCount) % documentCount;
    case 'Home':
      return 0;
    case 'End':
      return documentCount - 1;
    default:
      return undefined;
  }
}

function isCopyDocumentShortcut(event: globalThis.KeyboardEvent): boolean {
  return event.key === 'c' && (event.ctrlKey || event.metaKey);
}

export function JsonModal({ documents, isVisible }: { documents: JsonModalResolvedDocument[]; isVisible: boolean }) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { add: addToast } = useGlobalToasts();
  const monacoEditorRef = useRef<editor.IStandaloneCodeEditor | null>(null);

  const activeDocument = documents[selectedIndex];
  const handleCopyActiveDocument = useCallback(async () => {
    if (activeDocument == null) {
      return;
    }
    const copied = await copyText(activeDocument.content);
    if (!copied) {
      return;
    }
    addToast({
      text: <FormattedMessage {...messages.jsonCopiedToClipboard} />,
      timeout: 1000,
      variant: NotificationVariants.Positive,
    });
  }, [activeDocument, addToast]);

  const handleCopyShortcutKeyDown = useCallback(
    (event: globalThis.KeyboardEvent) => {
      if (!isVisible || !isCopyDocumentShortcut(event)) {
        return;
      }

      if (hasNonEmptyEditorSelection(monacoEditorRef.current)) {
        return;
      }

      void handleCopyActiveDocument();
      event.preventDefault();
    },
    [handleCopyActiveDocument, isVisible]
  );

  useEventListener('keydown', handleCopyShortcutKeyDown);

  if (!activeDocument) {
    return null;
  }

  const handleTabListKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const nextIndex = nextDocumentIndex(selectedIndex, documents.length, event.key);
    if (nextIndex == null || nextIndex === selectedIndex) {
      return;
    }

    event.preventDefault();
    setSelectedIndex(nextIndex);
    const nextDocument = documents[nextIndex];
    if (nextDocument != null) {
      document.getElementById(tabElementId(nextDocument.id))?.focus();
    }
  };

  return (
    <JsonModalContent>
      <JsonModalHeader data-testid="json-modal-controls">
        <Tabs
          selectedIndex={selectedIndex}
          onSelect={setSelectedIndex}
          appearance={TabAppearance.Underlined}
          size={TabSize.Default}
          flex={1}
        >
          <JsonModalTabBar>
            <TabList suppressOverflowList onKeyDown={handleTabListKeyDown}>
              {documents.map((document, index) => (
                <Tab
                  key={document.id}
                  id={tabElementId(document.id)}
                  label={document.title}
                  tabIndex={index === selectedIndex ? 0 : -1}
                />
              ))}
            </TabList>
          </JsonModalTabBar>
        </Tabs>
        <JsonModalCopyHint>
          <Text size="fontSizeSmall" color="colorTextSubtle">
            <FormattedMessage {...messages.copyShortcutHint} values={{ shortcut: copyDocumentShortcutLabel() }} />
          </Text>
        </JsonModalCopyHint>
      </JsonModalHeader>
      <JsonModalTabPanel role="tabpanel" aria-labelledby={tabElementId(activeDocument.id)}>
        <JsonModalSourcePre data-testid="show-json-editor">{activeDocument.content}</JsonModalSourcePre>
        <JsonMonacoEditor value={activeDocument.content} editorRef={monacoEditorRef} />
      </JsonModalTabPanel>
    </JsonModalContent>
  );
}
